import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";

const AuthModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState("login");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [localError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    login,
    signup,
    requestPasswordReset,
    initiateGoogleAuth,
    error: authError,
    isAuthenticated,
  } = useAuth();

  // Reset google loading when auth completes (mobile OAuth doesn't reload page)
  React.useEffect(() => {
    if (isAuthenticated) {
      setGoogleLoading(false);
    }
  }, [isAuthenticated]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setLocalError("");
  };

  const validateForm = () => {
    if (mode === "signup") {
      if (!formData.username || formData.username.length < 3) {
        return "Username must be at least 3 characters";
      }
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        return "Please enter a valid email address";
      }
      if (!formData.password || formData.password.length < 8) {
        return "Password must be at least 8 characters";
      }
      if (formData.password !== formData.confirmPassword) {
        return "Passwords do not match";
      }
    } else {
      if (!formData.username || !formData.password) {
        return "Please fill in all fields";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLoading(true);
    setLocalError("");

    try {
      if (mode === "login") {
        await login(formData.username, formData.password);
      } else {
        await signup(formData.username, formData.email, formData.password);
      }

      // Success - close modal and reset form
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      onClose();
    } catch (err) {
      setLocalError(
        err.message || (mode === "login" ? "Login failed" : "Sign up failed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail || !/\S+@\S+\.\S+/.test(resetEmail)) {
      setLocalError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setLocalError("");

    try {
      await requestPasswordReset(resetEmail);
      setResetMessage("Password reset instructions sent to your email");
      setResetEmail("");
    } catch (err) {
      setLocalError(err.message || "Failed to send password reset email");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setLocalError("");
    setShowPasswordReset(false);
    setResetMessage("");
    setFormData({ username: "", email: "", password: "", confirmPassword: "" });
  };

  const togglePasswordReset = () => {
    setShowPasswordReset(!showPasswordReset);
    setLocalError("");
    setResetMessage("");
    setResetEmail("");
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setLocalError("");
      await initiateGoogleAuth();
      // User will be redirected to Google
    } catch (err) {
      setLocalError(err.message || "Failed to start Google sign in");
      setGoogleLoading(false);
    }
  };

  const displayError = localError || authError;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="relative p-6 rounded-lg shadow-xl w-96 max-w-md mx-4 glass-card bg-forest-800 text-forest-100">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-lg w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
          aria-label="Close"
        >
          &times;
        </button>

        {showPasswordReset ? (
          <div>
            <h2 className="text-xl mb-4 text-center">Reset Password</h2>
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <input
                type="email"
                placeholder="Enter your email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300"
                disabled={loading}
              />

              {displayError && (
                <div className="text-ember-500 text-sm">{displayError}</div>
              )}
              {resetMessage && (
                <div className="text-leaf-500 text-sm">{resetMessage}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-md font-medium transition-colors btn-gold bg-gold-500 hover:bg-gold-400 text-forest-950 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <button
              onClick={togglePasswordReset}
              className="mt-4 text-sm text-gold-500 hover:text-gold-400"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <div>
            <h2 className="text-xl mb-4 text-center">
              {mode === "login" ? "Login" : "Sign Up"}
            </h2>

            {/* Google Sign In Button */}
            <div className="mb-4">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading || googleLoading}
                className="w-full py-2 px-4 rounded-md font-medium flex items-center justify-center gap-2 border transition-colors bg-white text-gray-900 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* Google Logo SVG */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                      fill="#34A853"
                    />
                    <path
                      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                      fill="#EA4335"
                    />
                  </g>
                </svg>
                {googleLoading
                  ? "Connecting..."
                  : `Sign ${mode === "login" ? "in" : "up"} with Google`}
              </button>
            </div>

            {/* Divider - centered "or" text with lines */}
            <div className="flex items-center justify-center my-4 w-full">
              <div className="flex-1 border-t border-forest-600"></div>
              <span className="px-4 text-sm text-center text-forest-300">
                or
              </span>
              <div className="flex-1 border-t border-forest-600"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="username"
                placeholder={
                  mode === "login" ? "Username or Email" : "Username"
                }
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300"
                disabled={loading}
                required
              />

              {mode === "signup" && (
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300"
                  disabled={loading}
                  required
                />
              )}

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300"
                disabled={loading}
                required
              />

              {mode === "signup" && (
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 bg-forest-700 border-forest-600 text-forest-100 placeholder-forest-300"
                  disabled={loading}
                  required
                />
              )}

              {displayError && (
                <div className="text-ember-500 text-sm">{displayError}</div>
              )}

              {mode === "signup" && (
                <p className="text-xs text-forest-300 text-center">
                  By creating an account, you agree to the{" "}
                  <a
                    href="/terms"
                    className="text-gold-400 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="text-gold-400 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-md font-medium transition-colors btn-gold bg-gold-500 hover:bg-gold-400 text-forest-950 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? mode === "login"
                    ? "Logging in..."
                    : "Creating Account..."
                  : mode === "login"
                    ? "Login"
                    : "Sign Up"}
              </button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <button
                onClick={toggleMode}
                className="text-sm text-gold-500 hover:text-gold-400"
              >
                {mode === "login"
                  ? "Need an account? Sign Up"
                  : "Have an account? Login"}
              </button>

              {mode === "login" && (
                <div>
                  <button
                    onClick={togglePasswordReset}
                    className="text-sm text-gold-500 hover:text-gold-400"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
